import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Typography } from "@faire/web--source/slate/Typography";
import React, { type JSX } from "react";

import { FileUploaderDropzone } from "./FileUploaderDropzone";
import { useFileUploaderState } from "./useFileUploaderState";

// @ts-prune-ignore-next
export interface IFileUploaderProps {
  /**
   * Invoked when the file is accepted in the dropzone
   */
  onUploadSuccess: (file: File) => Promise<void>;
  /**
   * Invoked when the file fails to be accepted in the dropzone
   */
  onUploadError: (message: string) => void;
  /**
   * Invoked when the user removes a chosen file
   */
  onRemoveFile: () => void;
  /**
   * Comma separated list of file extensions that are accepted
   * https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
   */
  accepts: string;
  /**
   * Max file size in bytes
   */
  maxFileSize: number;
  /**
   * If there's already a chosen file, the picker will be replaced with a box
   * displaying the file name
   */
  currentFile?: File;
  /**
   * Whether a file accepted by the uploader is still invalid
   */
  invalidFile?: boolean;
  /**
   * Size of the uploader box. Defaults to 200px
   */
  height?: number;
  /**
   * Size of the uploader box. Defaults to 100%
   */
  width?: number;
  /**
   * Text content that is shown in the center of the uploader
   * It can be used to let users know the type of files that are accepted
   */
  uploadCta?: string | JSX.Element;
  /**
   * Whether to show an icon in the center of the uploader
   */
  showFileIcon?: boolean;
}

export const FileUploader: React.FC<IFileUploaderProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const state = useFileUploaderState(
    props.onUploadSuccess,
    props.onUploadError
  );

  return (
    <>
      <FileUploaderDropzone
        currentFile={props.currentFile}
        uploadCta={props.uploadCta}
        height={props.height}
        width={props.width}
        maxFileSize={props.maxFileSize}
        accepts={props.accepts}
        strictLocalize={strictLocalize}
        onRemoveFile={props.onRemoveFile}
        onUploadSuccess={state.handleUploadSuccess}
        onUploadError={state.handleUploadError}
        loading={state.loading}
        error={!!state.errorMessage || props.invalidFile}
        showFileIcon={props.showFileIcon}
      />
      <ErrorMessage error={state.errorMessage} />
    </>
  );
};

interface IErrorMessageProps {
  error?: string;
}

const ErrorMessage: React.FC<IErrorMessageProps> = ({ error }) => {
  if (!error) {
    return null;
  }

  return (
    <Typography
      color={Core.text.critical}
      variant="labelSansRegular"
      style={{ marginTop: 3 }}
    >
      {error}
    </Typography>
  );
};
